/**
 * Mutations on the conditional-logic graph.
 *
 * The builder wires branches from both directions — from a controller's answer
 * ("when they pick this, ask…") and from a follow-up ("only show this when…").
 * Both end up writing the same two fields, so they share these operations
 * rather than each hand-rolling the `optionsJson` edits.
 *
 * A target is referenced by the stable `branchKey` stored on the target
 * question itself, minted the first time something points at it. Clearing the
 * last reference also clears the key, which is what distinguishes a question
 * that is deliberately always shown from one that was left half-wired.
 */

import { parseFieldOptions, patchFieldOptions } from './questionOptionsJson';

export interface BranchOpQuestion {
  id?: string | null;
  optionsJson?: string | null;
}

const newBranchKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `bk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/** Ensure `questionId` carries a branchKey, returning the list and the key. */
export function ensureBranchKey<T extends BranchOpQuestion>(
  questions: T[],
  questionId: string
): { questions: T[]; branchKey: string | null } {
  const target = questions.find(q => q.id === questionId);
  if (!target) return { questions, branchKey: null };

  const existing = parseFieldOptions(target.optionsJson).branchKey;
  if (existing) return { questions, branchKey: existing };

  const branchKey = newBranchKey();
  return {
    questions: questions.map(q =>
      q.id === questionId ? { ...q, optionsJson: patchFieldOptions(q.optionsJson, { branchKey }) } : q
    ),
    branchKey,
  };
}

/**
 * Wire "answer `optionIndex` of `controllerId` reveals `targetId`".
 * Replaces whatever that answer previously pointed at.
 */
export function linkAnswerToQuestion<T extends BranchOpQuestion>(
  questions: T[],
  controllerId: string,
  optionIndex: number,
  targetId: string
): T[] {
  if (controllerId === targetId) return questions;

  const { questions: withKey, branchKey } = ensureBranchKey(questions, targetId);
  if (!branchKey) return questions;

  return withKey.map(q => {
    if (q.id !== controllerId) return q;
    const parsed = parseFieldOptions(q.optionsJson);
    return {
      ...q,
      optionsJson: patchFieldOptions(q.optionsJson, {
        optionTargets: { ...parsed.optionTargets, [String(optionIndex)]: branchKey },
      }),
    };
  });
}

/**
 * Clear the follow-up on one answer. If that was the last reference to the
 * former target, drop its branchKey too so it reads as "always shown" rather
 * than as a disconnected follow-up.
 */
export function unlinkAnswer<T extends BranchOpQuestion>(
  questions: T[],
  controllerId: string,
  optionIndex: number
): T[] {
  const controller = questions.find(q => q.id === controllerId);
  if (!controller) return questions;

  const parsed = parseFieldOptions(controller.optionsJson);
  const releasedKey = parsed.optionTargets[String(optionIndex)];
  if (!releasedKey) return questions;

  const nextTargets = { ...parsed.optionTargets };
  delete nextTargets[String(optionIndex)];

  const next = questions.map(q =>
    q.id === controllerId
      ? { ...q, optionsJson: patchFieldOptions(q.optionsJson, { optionTargets: nextTargets }) }
      : q
  );

  return isBranchKeyReferenced(next, releasedKey) ? next : clearBranchKey(next, releasedKey);
}

/**
 * Detach a question from every answer that reveals it and clear its branchKey,
 * turning it into a question every customer sees.
 */
export function detachQuestion<T extends BranchOpQuestion>(
  questions: T[],
  questionId: string
): T[] {
  const target = questions.find(q => q.id === questionId);
  if (!target) return questions;

  const branchKey = parseFieldOptions(target.optionsJson).branchKey;
  if (!branchKey) return questions;

  const cleared = questions.map(q => {
    const parsed = parseFieldOptions(q.optionsJson);
    const remaining = Object.fromEntries(
      Object.entries(parsed.optionTargets).filter(([, value]) => value !== branchKey)
    );
    if (Object.keys(remaining).length === Object.keys(parsed.optionTargets).length) return q;
    return { ...q, optionsJson: patchFieldOptions(q.optionsJson, { optionTargets: remaining }) };
  });

  return clearBranchKey(cleared, branchKey);
}

/**
 * Remove a question and repair the graph: drop answers that pointed at it, and
 * release any follow-up that only it revealed.
 */
export function removeQuestion<T extends BranchOpQuestion>(
  questions: T[],
  questionId: string
): T[] {
  const target = questions.find(q => q.id === questionId);
  if (!target) return questions;

  const parsed = parseFieldOptions(target.optionsJson);
  const orphanedKeys = Object.values(parsed.optionTargets);
  const detached = parsed.branchKey ? detachQuestion(questions, questionId) : questions;

  let next = detached.filter(q => q.id !== questionId);
  for (const key of orphanedKeys) {
    if (!isBranchKeyReferenced(next, key)) next = clearBranchKey(next, key);
  }
  return next;
}

function isBranchKeyReferenced<T extends BranchOpQuestion>(questions: T[], branchKey: string): boolean {
  return questions.some(q =>
    Object.values(parseFieldOptions(q.optionsJson).optionTargets).includes(branchKey)
  );
}

function clearBranchKey<T extends BranchOpQuestion>(questions: T[], branchKey: string): T[] {
  return questions.map(q => {
    if (parseFieldOptions(q.optionsJson).branchKey !== branchKey) return q;
    return { ...q, optionsJson: patchFieldOptions(q.optionsJson, { branchKey: undefined }) };
  });
}
